import { useEffect, useState } from "react";
import { PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/outline";
import {
  createUser,
  getUsers,
  deleteUser,
  updateUser,
} from "../../api/usersApi";
import { getUserTypes } from "../../api/userTypesApi";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import PageLoader from "../../components/pageLoader";
import UserFormModal from "./UserFormModal";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";

const UsersPage = () => {
  const [showAddModal, setShowAddModal] = useState(false);

  const LIMIT = 10;

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [roles, setRoles] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const handleUserSubmit = async (payload) => {
    try {
      setSaving(true);

      if (editingUser) {
        await updateUser(editingUser.id, payload);
      } else {
        await createUser(payload);
      }

      setEditingUser(null);
      setShowAddModal(false);

      // Refresh list
      const offset = (page - 1) * LIMIT;
      const data = await getUsers(LIMIT, offset);
      setUsers(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.detail ||
          "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setDeleting(true);
      await deleteUser(deleteUserId);

      setDeleteUserId(null);

      // Refresh users list
      const offset = (page - 1) * LIMIT;
      const data = await getUsers(LIMIT, offset);
      setUsers(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      alert("Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError("");

        const offset = (page - 1) * LIMIT;
        const data = await getUsers(LIMIT, offset);

        setUsers(data.results || []);
        setTotal(data.count || 0);
      } catch (err) {
        setError("Failed to load users.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [page]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const data = await getUserTypes();
        const list = Array.isArray(data) ? data : data.results || [];
        setRoles(list);
      } catch (err) {
        console.error("Failed to load roles");
      }
    };

    fetchRoles();
  }, []);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Users</h1>
          <p className="text-sm text-slate-400">
            Manage system users and their roles
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          <PlusIcon className="w-4 h-4" />
          Add User
        </button>
      </div>

      {error && <div className="p-6 text-red-400">{error}</div>}

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4">ID</th>
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Email</th>
                <th className="px-6 py-4">Phone</th>
                <th className="px-6 py-4">Role</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-white/5 last:border-none hover:bg-slate-800/40 transition"
                >
                  <td className="px-6 py-4 text-sm text-slate-300">
                    {user.id}
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-white">
                      {user.first_name} {user.last_name}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {user.email}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {user.phone}
                  </td>

                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-800 text-slate-200 border border-white/10">
                      {user.type?.name}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingUser(user)}
                        className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => setDeleteUserId(user.id)}
                        className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition"
                        title="Delete user"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state placeholder (future) */}
        {users.length === 0 && (
          <div className="p-8 text-center text-slate-400">No users found.</div>
        )}
      </div>

      <div className="flex justify-end">
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
            ${
              page === pageNumber
                ? "bg-pink-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <UserFormModal
        isOpen={showAddModal || !!editingUser}
        onClose={() => {
          setShowAddModal(false);
          setEditingUser(null);
        }}
        onSubmit={handleUserSubmit}
        loading={saving}
        mode={editingUser ? "edit" : "create"}
        initialData={editingUser}
        roles={roles}
      />

      <ConfirmDeleteModal
        isOpen={!!deleteUserId}
        onClose={() => setDeleteUserId(null)}
        onConfirm={handleDeleteUser}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default UsersPage;
